import {
  getYouTubeVideoId,
  isInternalResourceUrl,
  isYouTubeVideoHost,
  normalizeResourceUrl,
} from "./agendaResources";
import { normalizeCanvaManualUrl, stripCanvaEmbedParam } from "./canvaUtils";
import {
  buildGoogleSlidesEmbedSrc,
  extractGoogleSlidesPresentationKey,
  isGoogleSlidesPresentationUrl,
} from "./googleSlidesLinks";

const YOUTUBE_CHANNEL_HANDLE_REGEX =
  /^https?:\/\/(?:www\.)?youtube\.com\/@[^/?#\s]+\/?$/i;

const DRIVE_FILE_SHARE_REGEX =
  /^https?:\/\/drive\.google\.com\/file\/d\/([^/]+)\/view(?:\?.*)?$/i;

const DRIVE_FILE_PREVIEW_REGEX =
  /^https?:\/\/drive\.google\.com\/file\/d\/([^/]+)\/preview(?:\?.*)?$/i;

/** Match iframe markup teachers paste from Genially, Canva, etc. */
const IFRAME_SRC_REGEX = /<iframe\b[^>]*\bsrc\s*=\s*["']([^"']+)["']/i;

function decodeEncodedIframeMarkup(value) {
  if (!/%3Ciframe/i.test(value) || /<iframe\b/i.test(value)) {
    return value;
  }
  try {
    return decodeURIComponent(value);
  } catch (_) {
    return value;
  }
}

/**
 * Pull a usable URL out of pasted iframe HTML, or return null.
 * Decodes common HTML entities like &amp; in query strings.
 */
export function extractIframeWebsiteSrc(rawValue) {
  let value = decodeEncodedIframeMarkup(String(rawValue ?? "").trim());
  if (!value || !/<iframe\b/i.test(value)) {
    return null;
  }

  const match = value.match(IFRAME_SRC_REGEX);
  const src = match?.[1]?.replace(/&amp;/gi, "&").trim();
  return src || null;
}

function looksLikeIframeMarkup(value) {
  return /<iframe\b/i.test(value) || /%3Ciframe/i.test(value);
}

const buildYouTubeEmbedUrl = (rawValue) => {
  const videoId = getYouTubeVideoId(rawValue);
  if (!videoId) return "";

  try {
    const parsedUrl = new URL(normalizeResourceUrl(rawValue));
    const startTime = parsedUrl.searchParams.get("start") || parsedUrl.searchParams.get("t");
    if (startTime) {
      return `https://www.youtube.com/embed/${videoId}?start=${startTime}`;
    }
  } catch (_) {
    // Fall through to the plain embed URL.
  }

  return `https://www.youtube.com/embed/${videoId}`;
};

const buildYouTubeWatchUrl = (rawValue) => {
  const videoId = getYouTubeVideoId(rawValue);
  if (!videoId) return "";

  try {
    const parsedUrl = new URL(normalizeResourceUrl(rawValue));
    const hostname = parsedUrl.hostname.replace(/^www\./i, "").toLowerCase();
    if (!isYouTubeVideoHost(hostname) || YOUTUBE_CHANNEL_HANDLE_REGEX.test(parsedUrl.toString())) {
      return "";
    }

    const startTime = parsedUrl.searchParams.get("start") || parsedUrl.searchParams.get("t");
    const watchUrl = new URL(`https://www.youtube.com/watch?v=${videoId}`);
    if (startTime) {
      watchUrl.searchParams.set("t", startTime);
    }
    return watchUrl.toString();
  } catch (_) {
    return `https://www.youtube.com/watch?v=${videoId}`;
  }
};

/**
 * Normalize a pasted/typed website URL for iframe embeds (Web Box, Quick Launch Links, etc.).
 * Returns "" for empty input so callers never persist undefined.
 *
 * Converts common share URLs into iframe-friendly forms when possible
 * (YouTube watch/share → embed, Google Drive view → preview,
 * Google Slides edit/share → embed).
 * Also accepts full iframe embed HTML and keeps only the src URL.
 */
export function normalizeIframeWebsiteLink(rawValue) {
  let value = String(rawValue ?? "").trim();
  if (!value) {
    return "";
  }

  // Remove @ prefix if present (used as a marker but not part of URL)
  if (value.startsWith("@")) {
    value = value.substring(1).trim();
    if (!value) return "";
  }

  if (isInternalResourceUrl(value)) {
    return value;
  }

  const iframeSrc = extractIframeWebsiteSrc(value);
  if (iframeSrc) {
    value = iframeSrc;
  } else if (/<iframe\b/i.test(value)) {
    // Paste looked like embed HTML but had no usable src — don't invent https://<iframe...>.
    return "";
  }

  value = normalizeResourceUrl(value);
  if (!value) return "";

  // Convert Google Drive share links to iframe-friendly preview URLs
  if (DRIVE_FILE_SHARE_REGEX.test(value)) {
    const fileId = value.match(DRIVE_FILE_SHARE_REGEX)[1];
    return `https://drive.google.com/file/d/${fileId}/preview`;
  }

  if (isGoogleSlidesPresentationUrl(value)) {
    return buildGoogleSlidesEmbedSrc(value, { showControls: false });
  }

  // Pass through channel handle URLs like https://www.youtube.com/@channelname
  if (YOUTUBE_CHANNEL_HANDLE_REGEX.test(value)) {
    return value;
  }

  const youtubeEmbedUrl = buildYouTubeEmbedUrl(value);
  if (youtubeEmbedUrl) {
    return youtubeEmbedUrl;
  }

  const canvaEmbedUrl = normalizeCanvaManualUrl(value);
  if (canvaEmbedUrl) {
    return canvaEmbedUrl;
  }

  return value;
}

/**
 * Convert an iframe-oriented website URL into a normal browser / new-tab URL.
 * YouTube embed → watch, Google Drive preview → view. Other URLs pass through.
 */
export function toBrowserWebsiteLink(rawValue) {
  let value = String(rawValue ?? "").trim();
  if (!value) {
    return "";
  }

  if (isInternalResourceUrl(value)) {
    return value;
  }

  const iframeSrc = extractIframeWebsiteSrc(value);
  if (iframeSrc) {
    value = iframeSrc;
  } else if (looksLikeIframeMarkup(value)) {
    return "";
  }

  value = normalizeResourceUrl(value);
  if (!value) return "";

  if (DRIVE_FILE_PREVIEW_REGEX.test(value)) {
    const fileId = value.match(DRIVE_FILE_PREVIEW_REGEX)[1];
    return `https://drive.google.com/file/d/${fileId}/view`;
  }

  if (isGoogleSlidesPresentationUrl(value)) {
    const parsedUrl = new URL(value);
    if (/\/embed\/?$/i.test(parsedUrl.pathname)) {
      const presentationKey = extractGoogleSlidesPresentationKey(value);
      return `https://docs.google.com/presentation/d/${presentationKey}/edit`;
    }
  }

  if (YOUTUBE_CHANNEL_HANDLE_REGEX.test(value)) {
    return value;
  }

  const youtubeWatchUrl = buildYouTubeWatchUrl(value);
  if (youtubeWatchUrl) {
    return youtubeWatchUrl;
  }

  const canvaEmbedUrl = normalizeCanvaManualUrl(value);
  if (canvaEmbedUrl) {
    return stripCanvaEmbedParam(canvaEmbedUrl);
  }

  return value;
}

/**
 * Resolve a TinyEditor / content-box anchor href into something window.open
 * can load. Pasted iframe HTML becomes the embed src (YouTube → watch URL).
 * Returns "" when the href is not a real http(s)/mailto/tel/sms URL.
 */
export function resolveOpenableWebsiteHref(rawHref) {
  const href = String(rawHref ?? "").trim();
  if (!href) {
    return "";
  }

  if (/^(mailto:|tel:|sms:)/i.test(href)) {
    return href;
  }

  const browserLink = toBrowserWebsiteLink(href);
  return isSavableIframeWebsiteLink(browserLink, { allowInternal: true })
    ? browserLink
    : "";
}

/**
 * Whether a normalized iframe website link is safe to persist / launch.
 * Rejects empty and protocol-only values. Optionally allows internal app paths.
 */
export function isSavableIframeWebsiteLink(link, { allowInternal = false } = {}) {
  const value = String(link ?? "").trim();
  if (!value) return false;
  if (/^https?:\/\/$/i.test(value)) return false;

  if (allowInternal && isInternalResourceUrl(value)) {
    return true;
  }

  try {
    const url = new URL(value);
    if (!url.hostname) return false;
    if (url.hostname === "localhost") return true;
    return url.hostname.includes(".");
  } catch (_) {
    return false;
  }
}
